import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import parse from 'html-react-parser';
import { toast } from 'react-toastify';

//Custom components
import { CustomTab, Loader } from '../../../Component/Others';
import { DataTableComponent } from '../../../Component/DataTable';
import { APIService } from '../../../Service';
import { EmptyScreen } from '../../../Component/EmptyComponent';

const TAB_DATA = [
    {
        id: 1,
        label: "Overview"
    },
    {
        id: 2,
        label: "Redeemed users"
    },
];


const ITEMS_PER_PAGE = 20;

const PAGE_SORT_FIELDS = [
    'user_id',
    'name',
    'redeemed_on',
    'status',
    'reference_id',
];

const RewardDetail = () => {

    const navigate = useNavigate();
    const { state } = useLocation();
    const scrollRef = useRef(null);
    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);
    const PROFILE_DATA = useSelector((store) => store.Reducer.PROFILE_DATA);

    const [tabIndex, setTabIndex] = useState(0);

    const [currentPage, setCurrentPage] = useState(1);

    const [loading, setLoading] = useState(true);
    const [detailData, setDetailData] = useState({});

    const [usersList, setUsersList] = useState({ data: [] });
    const [userLoading, setUserLoading] = useState(false);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });
    }, []);

    useEffect(() => {
        if (state !== null) {
            if (state?.tab_index) {
                setTabIndex(state.tab_index);
            } else {
                setTabIndex(1);
            }
        } else {
            setTabIndex(1);
        }

    }, [state])

    useEffect(() => {
        if (state !== null) {
            if (state.id) {
                _getRewardDetail();
            }
        } else {
            navigate("/rewards", {
                state: {
                    ...state
                }
            });
        }
    }, []);

    useEffect(() => {
        if (tabIndex === 2) {
            if (skipListFetchRef.current) {
                skipListFetchRef.current = false;
                return;
            }
            setUserLoading(true);
            _getUsers();
        }
    }, [tabIndex, currentPage]);

    // handle tab change 
    const _handleTabChange = (id) => {
        setSortField(null);
        setSortDirection(null);
        setCurrentPage(1);
        navigate("/rewards/detail", {
            state: {
                ...state,
                tab_index: id
            }
        });
    };


    // handle paginate store list 
    const _handlePageChange = (page_num) => {
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);
        setUserLoading(true);

        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !PAGE_SORT_FIELDS.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setUserLoading(true);

        // Only skip the list useEffect when page will actually change.
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _getUsers(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    const TABLE_COLUMNS = [
        {
            name: 'User id',
            id: 'user_id',
            sortField: 'user_id',
            cell: row => <>{row.user_id || "-"}</>,
            width: "120px",
            sortable: true,
        },
        {
            name: 'Name',
            id: 'name',
            sortField: 'name',
            cell: row => <>{row.name || "-"}</>,
            selector: row => row.name,
            width: "320px",
            sortable: true,
        },
        {
            name: 'Redeemed on',
            id: 'redeemed_on',
            sortField: 'redeemed_on',
            selector: row => row.redeemed_on || "-",
            cell: row => <>{row.redeemed_on || "-"}</>,
            width: "200px",
            format: row => row.redeemed_on,
            sortable: true,
        },
        {
            name: 'Status',
            id: 'status',
            sortField: 'status',
            cell: row => <span className={`${row.status === "Success" ? "e-text-philippine-green " : "e-text-boston-university-red"} e-font-weight-600`}>{row.status}</span>,
            width: "110px",
            sortable: true,
        },
    ];

    // API - Get reward detail
    const _getRewardDetail = () => {
        let is_post = true;
        let url = 'CA/rewards/ygg-brands/detail';
        let data = {
            "id": state.id,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setDetailData(response.data);
            } else {
                navigate("/rewards", {
                    state: {
                        ...state
                    }
                });
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setLoading(false);
        });
    };

    // API - Get reward user list
    const _getUsers = (page_num = currentPage, sort_by = sortField, sort_order = sortDirection) => {
        const effective_sort_by = sort_by && PAGE_SORT_FIELDS.includes(sort_by) ? sort_by : null;
        const effective_sort_order = effective_sort_by ? sort_order : null;

        setUserLoading(true);
        let is_post = true;
        let url = 'CA/rewards/ygg-brands/users';
        let data = {
            "id": state.id,
            "page_num": page_num,
            "limit": ITEMS_PER_PAGE,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setUsersList(response);
            } else {
                setUsersList({ data: [] });
            }
            setUserLoading(false);
        });
    };


    return (
        <section className='pb-5 '>
            <div className={`e-bg-lavender-blush-light padding-12px-top padding-98px-bottom padding-52px-lr`}>
                <div className='e-display-flex cursor-pointer w-max-content'
                    onClick={() => navigate("/rewards", {
                        state: {
                            ...state,
                        }
                    })} >
                    <div>
                        <img src={require("../../../Assets/Image/CountryAdmin/arrow-left.svg").default}
                            draggable={false}
                            className='cursor-pointer'
                            alt='arrow'
                        />
                    </div>
                    <h1 className='ps-1 mb-0 e-primary-font-playfair e-font-24 line-height-32px e-text-rich-black e-font-weight-600'>
                        Reward detail
                    </h1>
                </div>
            </div>

            {loading ?
                <Loader />
                :
                <div className='margin-neg-66px-top padding-52px-lr'>

                    <div className='e-bg-white e-admin-box-shadow padding-32px-tb padding-40px-lr e-border-radius-16 '>

                        <div className='row'>
                            <div className='col-8 my-auto'>
                                <div className='d-flex align-items-center gap-3'>
                                    <div className='e-bg-platinum rounded-circle overflow-hidden ' style={{ width: "fit-content", minWidth: "84px", height: "84px" }}>
                                        {detailData.logo &&
                                            <img src={detailData.logo}
                                                className='w-100 h-100 rounded-circle padding-2px-all'
                                                alt='logo'
                                            />
                                        }
                                    </div>

                                    <p className='mb-0 e-alt-font-noto-sans  e-font-20 line-height-28px e-text-rich-black e-font-weight-600'>
                                        {detailData.title}
                                    </p>

                                    <div className='d-inline-flex e-bg-spanish-yellow py-1 px-2 e-border-radius-16 e-alt-font-noto-sans e-text-white e-font-14 e-font-weight-700 line-height-14px'>
                                        {/* <img src={require("../../../Assets/Image/CountryAdmin/ManageCompetition/streak.svg").default}
                                            width={16}
                                            height={16}
                                            alt="streak" /> */}
                                        {detailData.points}
                                        <span className='e-font-weight-400 e-font-12 ps-1'>{detailData.points > 0 ? "points" : "point"}</span>
                                    </div>
                                </div>
                            </div>

                            <div className='col-4 ps-0'>
                                <div className='e-bg-ghost-white p-3 e-border-radius-8'>
                                    <div className='row row-col-2'>
                                        <div className='col'>
                                            <p className='mb-4 e-alt-font-noto-sans e-text-davys-grey e-font-14 e-font-weight-400 line-height-20px '>
                                                Redeemed users
                                            </p>
                                            <p className='mb-0 e-alt-font-noto-sans e-text-rich-black  e-font-14 e-font-weight-400 line-height-20px '>
                                                {detailData.redeemed_users}
                                            </p>
                                        </div>
                                        <div className='col'>
                                            <p className='mb-4 e-alt-font-noto-sans e-text-davys-grey e-font-14 e-font-weight-400 line-height-20px '>
                                                Status
                                            </p>
                                            <p className={`${detailData.status === true ? "e-text-philippine-green " : "e-text-boston-university-red"} mb-0 e-alt-font-noto-sans  e-font-14 e-font-weight-600 line-height-20px `}>
                                                {detailData.status === true ? "Active" : "Inactive"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    <div className='d-flex gap-3 overflow-auto py-4 e-tab-scroll'>
                        {TAB_DATA.map((tab) => (
                            <CustomTab key={tab.id}
                                data={tab}
                                tabIndex={tabIndex}
                                handleTabChange={_handleTabChange} />
                        ))}
                    </div>


                    {tabIndex === 1 ?
                        <div className='e-bg-white e-border-radius-16 p-4'>
                            <div className='d-flex flex-column gap-4'>
                                {PROFILE_DATA?.country_id === 8 ?
                                    <>
                                        <div>
                                            <p className='mb-2 e-alt-font-noto-sans e-text-raisin-black  e-font-16 e-font-weight-500 line-height-22px'>
                                                How to Redeem
                                            </p>
                                            <ul className='ps-4 mb-0'>
                                                <li className='e-alt-font-noto-sans e-text-dark-liver e-font-14 e-font-weight-400 line-height-24px'>
                                                    Open the Goubba Wallet app.
                                                </li>
                                                <li className='e-alt-font-noto-sans e-text-dark-liver e-font-14 e-font-weight-400 line-height-24px'>
                                                    Sign in to your Goubba account.
                                                </li>
                                                <li className='e-alt-font-noto-sans e-text-dark-liver e-font-14 e-font-weight-400 line-height-24px'>
                                                    Navigate to Redeem Code or Coupons.
                                                </li>
                                                <li className='e-alt-font-noto-sans e-text-dark-liver e-font-14 e-font-weight-400 line-height-24px'>
                                                    Enter the voucher code exactly as provided.
                                                </li>
                                                <li className='e-alt-font-noto-sans e-text-dark-liver e-font-14 e-font-weight-400 line-height-24px'>
                                                    Confirm the redemption.
                                                </li>
                                                <li className='e-alt-font-noto-sans e-text-dark-liver e-font-14 e-font-weight-400 line-height-24px'>
                                                    The voucher value will be credited to your account or applied to the eligible service.
                                                </li>
                                            </ul>
                                        </div>

                                        <div>
                                            <p className='mb-2 e-alt-font-noto-sans e-text-raisin-black  e-font-16 e-font-weight-500 line-height-22px'>
                                                Validity
                                            </p>
                                            <p className='mb-0 e-text-dark-liver e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-16px'>
                                                valid from {detailData.validity_months} months from the date of redemption
                                            </p>
                                        </div>

                                        <div>
                                            <p className='mb-2 e-alt-font-noto-sans e-text-raisin-black  e-font-16 e-font-weight-500 line-height-22px'>
                                                Important Information
                                            </p>
                                            <ul className='ps-4 mb-0'>
                                                <li className='e-alt-font-noto-sans e-text-dark-liver e-font-14 e-font-weight-400 line-height-24px'>
                                                    Each voucher code can be redeemed only once.
                                                </li>
                                                <li className='e-alt-font-noto-sans e-text-dark-liver e-font-14 e-font-weight-400 line-height-24px'>
                                                    Keep your voucher code confidential until you redeem it.
                                                </li>
                                                <li className='e-alt-font-noto-sans e-text-dark-liver e-font-14 e-font-weight-400 line-height-24px'>
                                                    Enter the code exactly as displayed.
                                                </li>
                                                <li className='e-alt-font-noto-sans e-text-dark-liver e-font-14 e-font-weight-400 line-height-24px'>
                                                    Do not share your voucher code with anyone.
                                                </li>
                                                <li className='e-alt-font-noto-sans e-text-dark-liver e-font-14 e-font-weight-400 line-height-24px'>
                                                    Once redeemed, the voucher cannot be redeemed again.
                                                </li>
                                                <li className='e-alt-font-noto-sans e-text-dark-liver e-font-14 e-font-weight-400 line-height-24px'>
                                                    Redeemed vouchers cannot be transferred to another account.
                                                </li>
                                                <li className='e-alt-font-noto-sans e-text-dark-liver e-font-14 e-font-weight-400 line-height-24px'>
                                                    Voucher redemption requires an active Goubba account.
                                                </li>
                                                <li className='e-alt-font-noto-sans e-text-dark-liver e-font-14 e-font-weight-400 line-height-24px'>
                                                    An internet connection may be required to redeem the voucher.
                                                </li>
                                                <li className='e-alt-font-noto-sans e-text-dark-liver e-font-14 e-font-weight-400 line-height-24px'>
                                                    If the code is reported as invalid or already redeemed, contact customer support with your transaction reference.
                                                </li>
                                            </ul>
                                        </div>
                                    </>
                                    :
                                    <>
                                        <div>
                                            <p className='mb-2 e-alt-font-noto-sans e-text-raisin-black  e-font-16 e-font-weight-500 line-height-22px'>
                                                Description
                                            </p>
                                            <p className='mb-0 e-text-dark-liver e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px'>
                                                {detailData.description}
                                            </p>
                                        </div>

                                        <div>
                                            <p className='mb-2 e-alt-font-noto-sans e-text-raisin-black  e-font-16 e-font-weight-500 line-height-22px'>
                                                Validity
                                            </p>
                                            <p className='mb-0 e-text-dark-liver e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-16px'>
                                                valid from {detailData.validity_months} months from the date of redemption
                                            </p>
                                        </div>

                                        <div>
                                            <p className='mb-2 e-alt-font-noto-sans e-text-raisin-black  e-font-16 e-font-weight-500 line-height-22px'>
                                                Terms and conditions
                                            </p>
                                            <div className='e-html-parser'>
                                                {detailData.details ? parse(detailData.details) : null}
                                            </div>
                                        </div>
                                    </>
                                }
                            </div>
                        </div>
                        :
                        <div ref={scrollRef} className='e-bg-white e-border-radius-16 py-4'>
                            <h6 className='mb-2 px-4 e-alt-font-noto-sans e-text-raisin-black  e-font-16 e-font-weight-500 line-height-22px'>
                                Users list
                            </h6>
                            {userLoading ?
                                <Loader />
                                :
                                <>
                                    {usersList.data.length !== 0 ?
                                        <div className='e-table e-store-table e-reward-table'>
                                            <DataTableComponent
                                                key={`reward-users-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                                columns={TABLE_COLUMNS}
                                                pagination={true}
                                                data={usersList.data}
                                                paginationServer={true}
                                                paginationTotalRows={usersList.total_items}
                                                paginationDefaultPage={currentPage}
                                                paginationPerPage={ITEMS_PER_PAGE}
                                                paginationResetDefaultPage={false}
                                                sortServer={true}
                                                defaultSortFieldId={sortField}
                                                defaultSortAsc={sortDirection !== 'desc'}
                                                onSort={_handleSort}
                                                onChangePage={_handlePageChange}
                                            />
                                        </div>
                                        :
                                        // empty screen
                                        <div className='h-100vh py-5 my-5'>
                                            <EmptyScreen message="No data found" />
                                        </div>
                                    }
                                </>

                            }
                        </div>
                    }
                </div>
            }

        </section>

    )
}

export default RewardDetail;
